import { displayTextLabelValidator, getDisplayTextLabelValidator, nonEmptyStringValidator } from '@vista/omnichannel-components-domain';
import { Validator } from '@vista-digital/validator';
import { MemberPaymentCardWalletInternalAction } from './internal-action';
/**
 * Default display labels for the Member Payment Card Wallet component.
 */
export const defaultMemberPaymentCardWalletDisplayText = {
    addCardButtonLabel: 'Add credit card',
    addCardSubmitButtonLabel: 'Add card',
    cardNumberFormat: 'Ending in ${cardLastFourDigits}',
    cardExpiryDateLabel: 'EXP: ${expiringMonth}/${expiringYear}',
    makePrimaryCardButtonLabel: 'Make primary',
    primaryCardLabel: 'Primary card',
    expiredCardLabel: 'Expired',
    assignPrimaryCardFailedMessage: 'Failed to set this as your primary payment method.',
    addCardTermsAndConditionsFieldLabel: 'Yes, I agree to the Terms and Services of ${cinemaChainName}',
    removeCardPromptTitle: 'Remove card',
    removeCardPromptMessage: 'Are you sure you want to remove the card ending in ${cardLastFourDigits} from your card wallet?\n' +
        'Note: Pending transactions for this card will still be processed even after you remove it.',
    removeCardPromptConfirmButtonLabel: 'Remove',
    unableToRemovePrimaryCardPromptTitle: 'Cannot remove primary card',
    unableToRemovePrimaryCardPromptMessage: 'You cannot remove your primary card as this is your selected payment method for ABC Unlimited. Please assign primary status to another card before removing this card.',
    unableToRemovePrimaryCardPromptAcceptButtonLabel: 'Ok',
    promptCancelButtonLabel: 'Cancel',
    removeCardFailedMessage: 'Failed to remove this payment method.',
    removeCardSucceededMessage: "We've removed this payment method.",
    addCardSucceededMessage: "We've added this payment method to your wallet.",
    addCardFailedTitle: "Couldn't add card",
    addCardFailedMessage: 'We had an issue while adding your card. Please try adding your card again.',
    addCardFailedTryAgainButtonLabel: 'Try again',
    addPrimaryCardSucceededMessage: "We've added this to your wallet as your primary payment method.",
    makePrimaryCardPromptTitle: 'Make primary payment method',
    makePrimaryCardPromptMessage: 'Would you like to use this card as your default payment method for future ${cinemaChainName} transactions? You can change this at any time.',
    makePrimaryCardPromptAcceptButtonLabel: 'Yes, make primary',
    makePrimaryCardPromptDeclineButtonLabel: 'No, just save card',
    primaryCardTermsAndConditionsPromptTitle: 'Terms and Conditions',
    addPrimaryCardTermsAndConditionsPromptTitle: 'Make primary payment method?',
    addPrimaryCardTermsAndConditionsPromptMessage: 'By assigning your card, you are accepting this card as your new primary payment method for ABC Unlimited and ' +
        'future ${cinemaChainName} transactions. You can change this at any time.\nView Terms and Conditions',
    addPrimaryCardTermsAndConditionsPromptAcceptButtonLabel: 'Yes, make primary',
    addPrimaryCardTermsAndConditionsPromptDeclineButtonLabel: 'No, just save card',
    primaryCardTermsAndConditionsPromptMessage: 'By assigning your card ending in ${cardLastFourDigits}, you are accepting this card as your new primary payment method for ABC Unlimited and ' +
        'future ${cinemaChainName} transactions. You can change this at any time.\nView Terms and Conditions',
    primaryCardTermsAndConditionsPromptAcceptButtonLabel: 'Accept',
    subscriptionFrozenFailedPaymentErrorMessage: [
        'Your last subscription transaction failed. Review your primary payment method before ',
        {
            text: 'reactivating your subscription',
            internalAction: MemberPaymentCardWalletInternalAction.ReactivateMemberSubscription
        },
        '.'
    ],
    subscriptionFrozenExpiredPrimaryCardErrorMessage: "Your ABC Unlimited subscription is currently frozen as your previous payment didn't process successfully. Please assign a new primary payment method to reactivate your subscription.",
    lastPaymentFailedErrorMessage: "Your last subscription failed. Review your primary payment method. We'll try again soon.",
    lastPaymentFailedExpiredPrimaryCardErrorMessage: "Your last subscription failed. We'll try again soon. Change your primary payment method to retain your subscription benefits.",
    changedPrimaryCardFromExpiredSucceededMessage: "Your primary payment method has been successfully changed to card ending in ${cardLastFourDigits}. We will attempt to process your subscription fee soon. You don't need to do anything else.",
    reactivateSubscriptionPromptTitle: 'Reactivate your account',
    reactivateSubscriptionPromptMessage: 'When you reactivate your ABC Unlimited account your primary payment method (card ending in ${cardLastFourDigits}) will be charged ${price}. Your next payment due date will be a month from now.\nView Terms and Conditions',
    reactivateSubscriptionAcceptButtonLabel: 'Accept',
    reactivateSubscriptionSucceededMessage: 'Transaction successful! Your account has been reactivated!',
    reactivateSubscriptionFailedMessage: 'Transaction failed! Review your payment method and try again.',
    setPrimaryCardReactivateSubscriptionPromptTitle: 'Assign a primary payment method to reactivate',
    setPrimaryCardReactivateSubscriptionPromptMessage: 'Would you also like to reactivate your ABC Unlimited subscription using this payment method?\n' +
        "When you reactivate your ABC Unlimited account we'll charge ${price} to this payment method. This will be your new primary payment method for future transactions. Your next payment due date will be a month from now.\nView Terms and Conditions",
    setPrimaryCardReactivateSubscriptionPromptAcceptButtonLabel: 'Accept and reactivate',
    setPrimaryCardReactivateSubscriptionPromptDeclineButtonLabel: 'No, only save card',
    primaryCardExpiredWarningMessage: 'Your primary payment method has expired. Please add or assign a new primary payment method to ensure your ABC Unlimited benefits continue uninterrupted.',
    primaryCardExpiringSoonWarningMessage: 'Your primary payment method for ABC Unlimited is expiring soon. Assign new primary card to ensure your ABC Unlimited benefits continue uninterrupted.',
    thirdPartyPaymentProviderPhasingOutWarningMessage: "Direct debit payment methods are being phased out. You'll need to add a credit card as a payment method to continue your ABC Unlimited subscription.",
    failedToLoadDataTitle: 'Sorry, something went wrong.',
    failedToLoadDataMessage: 'Refresh your browser to try loading your card wallet again.',
    failedToInitialiseConnectorTitle: 'Sorry, something went wrong.',
    failedToInitialiseConnectorMessage: 'Refresh your browser to try loading this payment card method again.',
    addingPaymentCardMessage: 'Adding card',
    cardDeclinedErrorTitle: 'Sorry, your card was declined.',
    cardDeclinedErrorMessage: 'Please try a different card.',
    cardDeclinedTryAgainButtonLabel: 'Try again',
    changeSubscriptionToVistaPaymentFailedMessage: "We couldn't update the payment details for your card ending in ${cardLastFourDigits}. Please retry adding the card as your new payment method.",
    hasPrimaryCardAndUsingThirdPartyPaymentProviderErrorMessage: [
        "We've updated your primary card, but payments are still linked to your previous payment method. ",
        {
            text: 'Please retry adding the card ending in ${cardLastFourDigits} as your new payment method',
            internalAction: MemberPaymentCardWalletInternalAction.ChangePaymentProviderToVista
        },
        '.'
    ],
    changePaymentProviderToVistaSucceededMessage: "We've saved your card ending in ${cardLastFourDigits} as your new payment method.",
    primaryCardNotSetMessage: 'Please add or assign a primary card before proceeding.'
};
export const memberPaymentCardWalletDisplayTextValidator = Validator.object({
    addCardButtonLabel: nonEmptyStringValidator,
    addCardSubmitButtonLabel: nonEmptyStringValidator,
    cardNumberFormat: nonEmptyStringValidator,
    cardExpiryDateLabel: nonEmptyStringValidator,
    makePrimaryCardButtonLabel: nonEmptyStringValidator,
    primaryCardLabel: nonEmptyStringValidator,
    expiredCardLabel: nonEmptyStringValidator,
    assignPrimaryCardFailedMessage: displayTextLabelValidator,
    addCardTermsAndConditionsFieldLabel: displayTextLabelValidator,
    removeCardPromptTitle: nonEmptyStringValidator,
    removeCardPromptMessage: displayTextLabelValidator,
    removeCardPromptConfirmButtonLabel: nonEmptyStringValidator,
    unableToRemovePrimaryCardPromptTitle: nonEmptyStringValidator,
    unableToRemovePrimaryCardPromptMessage: displayTextLabelValidator,
    unableToRemovePrimaryCardPromptAcceptButtonLabel: nonEmptyStringValidator,
    promptCancelButtonLabel: nonEmptyStringValidator,
    removeCardFailedMessage: displayTextLabelValidator,
    removeCardSucceededMessage: displayTextLabelValidator,
    addCardSucceededMessage: displayTextLabelValidator,
    addCardFailedMessage: displayTextLabelValidator,
    addCardFailedTitle: nonEmptyStringValidator,
    addCardFailedTryAgainButtonLabel: nonEmptyStringValidator,
    addPrimaryCardSucceededMessage: displayTextLabelValidator,
    makePrimaryCardPromptTitle: nonEmptyStringValidator,
    makePrimaryCardPromptMessage: displayTextLabelValidator,
    makePrimaryCardPromptAcceptButtonLabel: nonEmptyStringValidator,
    makePrimaryCardPromptDeclineButtonLabel: nonEmptyStringValidator,
    setPrimaryCardReactivateSubscriptionPromptDeclineButtonLabel: nonEmptyStringValidator,
    primaryCardTermsAndConditionsPromptTitle: nonEmptyStringValidator,
    addPrimaryCardTermsAndConditionsPromptMessage: displayTextLabelValidator,
    addPrimaryCardTermsAndConditionsPromptDeclineButtonLabel: nonEmptyStringValidator,
    addPrimaryCardTermsAndConditionsPromptAcceptButtonLabel: nonEmptyStringValidator,
    addPrimaryCardTermsAndConditionsPromptTitle: nonEmptyStringValidator,
    primaryCardTermsAndConditionsPromptMessage: displayTextLabelValidator,
    primaryCardTermsAndConditionsPromptAcceptButtonLabel: nonEmptyStringValidator,
    subscriptionFrozenFailedPaymentErrorMessage: getDisplayTextLabelValidator(Object.values(MemberPaymentCardWalletInternalAction)),
    subscriptionFrozenExpiredPrimaryCardErrorMessage: displayTextLabelValidator,
    lastPaymentFailedErrorMessage: displayTextLabelValidator,
    lastPaymentFailedExpiredPrimaryCardErrorMessage: displayTextLabelValidator,
    changedPrimaryCardFromExpiredSucceededMessage: displayTextLabelValidator,
    reactivateSubscriptionPromptTitle: nonEmptyStringValidator,
    reactivateSubscriptionPromptMessage: displayTextLabelValidator,
    reactivateSubscriptionAcceptButtonLabel: nonEmptyStringValidator,
    reactivateSubscriptionSucceededMessage: displayTextLabelValidator,
    reactivateSubscriptionFailedMessage: displayTextLabelValidator,
    setPrimaryCardReactivateSubscriptionPromptTitle: nonEmptyStringValidator,
    setPrimaryCardReactivateSubscriptionPromptMessage: displayTextLabelValidator,
    primaryCardExpiredWarningMessage: displayTextLabelValidator,
    setPrimaryCardReactivateSubscriptionPromptAcceptButtonLabel: nonEmptyStringValidator,
    primaryCardExpiringSoonWarningMessage: displayTextLabelValidator,
    thirdPartyPaymentProviderPhasingOutWarningMessage: displayTextLabelValidator,
    failedToLoadDataTitle: nonEmptyStringValidator,
    failedToLoadDataMessage: displayTextLabelValidator,
    failedToInitialiseConnectorTitle: nonEmptyStringValidator,
    failedToInitialiseConnectorMessage: displayTextLabelValidator,
    addingPaymentCardMessage: displayTextLabelValidator,
    cardDeclinedErrorTitle: nonEmptyStringValidator,
    cardDeclinedErrorMessage: displayTextLabelValidator,
    cardDeclinedTryAgainButtonLabel: nonEmptyStringValidator,
    changeSubscriptionToVistaPaymentFailedMessage: displayTextLabelValidator,
    hasPrimaryCardAndUsingThirdPartyPaymentProviderErrorMessage: getDisplayTextLabelValidator(Object.values(MemberPaymentCardWalletInternalAction)),
    changePaymentProviderToVistaSucceededMessage: displayTextLabelValidator,
    primaryCardNotSetMessage: displayTextLabelValidator
});
